// @ts-check

/**
 * Upgrade Product — adds a linked add-on product to the cart whenever the
 * main product is added and the upgrade's tick box is ticked.
 *
 * One component per upgrade. A PDP can carry as many as the merchant adds;
 * each resolves its own product from its own metafield setting, owns its own
 * cart line, and is matched across the page by its variant ID.
 *
 * Flow-agnostic by design: it listens for the standard cart lines update
 * event from its own section, so the standard buy buttons, the personaliser
 * modal (which submits the same form) and any future flow all trigger the
 * upgrade add without knowing about it.
 *
 * This component owns EVERY tick box for its upgrade. The inline one lives in
 * the buy box; further copies are cloned from the block's <template> into any
 * [data-upgrade-slot] in the same section (currently the personaliser modal
 * footer). Because one component owns them all, keeping them in step is local
 * state rather than an event round-trip.
 *
 * Cart line contract (ops, keep stable): 'From SKU': <main variant SKU>.
 */

import { Component } from '@theme/component';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { readCartFromSections } from '@theme/product-form';

function cartAddUrl() {
  return typeof Theme !== 'undefined' ? Theme.routes.cart_add_url : '/cart/add.js';
}

function cartUrl() {
  return typeof Theme !== 'undefined' ? Theme.routes.cart_url : '/cart';
}

/**
 * Cross-component sync events. Both carry `upgradeVariantId`, which is how a
 * mirror in another section (the packaging options block) knows an event is
 * about the upgrade it displays.
 *
 * 'upgrade-product:change' — an upgrade's state changed (informs mirrors)
 * 'upgrade-product:set'    — a mirror asks the owning component to change
 */
export const UpgradeEvents = {
  change: 'upgrade-product:change',
  set: 'upgrade-product:set',
};

/**
 * Reads an upgrade component's state without depending on its class having
 * been defined yet — mirrors in other sections can run first, so they read
 * the server-rendered payload rather than a property on the element.
 *
 * @param {Element} component - An <upgrade-product-component> element.
 * @returns {{variantId: string, label: string, priceMinor: number, checked: boolean} | null}
 */
export function readUpgrade(component) {
  const payload = component.querySelector('script[ref="payload"]');
  if (!payload) return null;

  try {
    const data = JSON.parse(payload.textContent || '{}');
    if (data.upgradeVariantId == null) return null;

    const checkbox = /** @type {HTMLInputElement | null} */ (
      component.querySelector('input[ref="checkbox"]')
    );

    return {
      variantId: String(data.upgradeVariantId),
      label: String(data.label || ''),
      priceMinor: Number(data.priceMinor) || 0,
      checked: Boolean(checkbox?.checked),
    };
  } catch {
    // Malformed payload — treat as absent rather than throwing into a mirror.
    return null;
  }
}

/**
 * @typedef {object} UpgradeProductRefs
 * @property {HTMLInputElement} checkbox
 * @property {HTMLScriptElement} payload
 * @property {HTMLTemplateElement} [mirrorTemplate]
 *
 * @extends Component<UpgradeProductRefs>
 */
class UpgradeProductComponent extends Component {
  requiredRefs = [];
  #abortController = new AbortController();
  #adding = false;

  /** Tick boxes cloned into slots elsewhere on the page. */
  /** @type {HTMLInputElement[]} */
  #mirrors = [];

  /** Wrappers this component appended to slots, so it can clean them up. */
  /** @type {HTMLElement[]} */
  #mirrorHosts = [];

  connectedCallback() {
    super.connectedCallback();

    // Re-armed per connection: disconnecting aborts the previous one, and the
    // theme editor reconnects components after a section re-render.
    this.#abortController = new AbortController();

    if (!this.refs.payload || !this.refs.checkbox) return;

    this.data = JSON.parse(this.refs.payload.textContent || '{}');

    const { signal } = this.#abortController;

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate, { signal });
    document.addEventListener(UpgradeEvents.set, this.#onSet, { signal });

    this.refs.checkbox.addEventListener(
      'change',
      () => this.setChecked(this.refs.checkbox.checked),
      { signal }
    );

    // Slots may be parsed after this component upgrades, so wait for a
    // complete document before looking for them.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.#projectMirrors, { once: true, signal });
    } else {
      this.#projectMirrors();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();

    // Mirrors live outside this component's subtree, so they survive a
    // section re-render unless removed explicitly.
    for (const host of this.#mirrorHosts) host.remove();
    this.#mirrorHosts = [];
    this.#mirrors = [];
  }

  /** @returns {string} */
  get variantId() {
    return String(this.data?.upgradeVariantId ?? '');
  }

  /** @returns {boolean} */
  get checked() {
    return Boolean(this.refs.checkbox?.checked);
  }

  /**
   * Single entry point for state changes, whatever triggered them: keeps
   * every tick box this component owns in step, then tells the page.
   *
   * @param {boolean} checked
   */
  setChecked(checked) {
    if (this.refs.checkbox) this.refs.checkbox.checked = checked;
    for (const mirror of this.#mirrors) mirror.checked = checked;

    this.dispatchEvent(
      new CustomEvent(UpgradeEvents.change, {
        bubbles: true,
        detail: {
          checked,
          upgradeVariantId: this.data?.upgradeVariantId,
          label: this.data?.label,
          priceMinor: Number(this.data?.priceMinor) || 0,
        },
      })
    );
  }

  // ==========================================================================
  // Mirrors
  // ==========================================================================

  #projectMirrors = () => {
    const template = this.refs.mirrorTemplate;
    if (!template) return;

    const section = this.closest('.shopify-section');
    if (!section) return;

    for (const slot of section.querySelectorAll('[data-upgrade-slot]')) {
      if (!(slot instanceof HTMLElement)) continue;
      if (slot.querySelector(`[data-upgrade-mirror-for="${CSS.escape(this.variantId)}"]`)) continue;

      const host = document.createElement('div');
      host.className = 'upgrade-product-mirror';
      host.dataset.upgradeMirrorFor = this.variantId;
      host.appendChild(template.content.cloneNode(true));

      const input = /** @type {HTMLInputElement | null} */ (
        host.querySelector('input[ref="mirror"]')
      );
      if (!input) continue;

      input.checked = this.checked;
      input.addEventListener('change', () => this.setChecked(input.checked), {
        signal: this.#abortController.signal,
      });

      slot.appendChild(host);
      slot.hidden = false;

      this.#mirrors.push(input);
      this.#mirrorHosts.push(host);
    }
  };

  /** @param {CustomEvent} event */
  #onSet = (event) => {
    if (event.target === this) return;
    if (String(event.detail?.upgradeVariantId ?? '') !== this.variantId) return;

    const checked = Boolean(event.detail?.checked);
    if (checked === this.checked) return;

    this.setChecked(checked);
  };

  // ==========================================================================
  // Cart
  // ==========================================================================

  get #mainVariantSku() {
    const form = document.getElementById(this.data?.formId);
    const variantId = /** @type {HTMLInputElement | null} */ (
      form?.querySelector('input[name="id"]')
    )?.value;
    return (variantId && this.data?.skus?.[variantId]) || '';
  }

  /** @param {CartLinesUpdateEvent & Event} event */
  #onCartLinesUpdate = (event) => {
    if (!this.checked || this.#adding) return;
    if (event.target === this) return;
    if (/** @type {any} */ (event).action !== 'add') return;

    // Only react to adds originating from this section (the main PDP form).
    const section = this.closest('.shopify-section');
    if (!section || !(event.target instanceof Node) || !section.contains(event.target)) return;

    /** @type {any} */ (event).promise
      ?.then(async (result) => {
        const detail = result?.detail;
        if (detail?.didError) return;
        if (detail?.source && detail.source !== 'product-form-component') return;

        const addedQuantity = Number(detail?.itemCount) || 1;
        await this.#addUpgrade(this.data?.quantityMode === 'one' ? 1 : addedQuantity);
      })
      .catch(() => {});
  };

  /** @param {number} quantity */
  async #addUpgrade(quantity) {
    if (this.#adding) return;
    this.#adding = true;

    /** @type {Set<string>} */
    const sectionIdSet = new Set();
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIdSet.add(el.dataset.sectionId);
    }
    const sectionIds = [...sectionIdSet];

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [
          {
            merchandiseId: this.variantId,
            quantity,
          },
        ],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(cartAddUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: this.data.upgradeVariantId,
              quantity,
              properties: { 'From SKU': this.#mainVariantSku },
            },
          ],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const responseJson = await response.json();

      if (!response.ok || responseJson.status) {
        throw new Error(responseJson.message || 'Upgrade add failed');
      }

      // The sections just returned already carry the cart JSON, so the drawer
      // does not have to wait on a second round trip to see the upgrade line.
      // Only fall back to the network when no cart section came back.
      const cart =
        readCartFromSections(responseJson.sections) ??
        (await fetch(`${cartUrl()}.json`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        }).then((r) => r.json()));

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'upgrade-product-component',
          sourceId: this.id || 'upgrade-product',
          itemCount: quantity,
          sections: responseJson.sections,
          didError: false,
        },
      });
    } catch (error) {
      console.error('[upgrade-product] add failed:', error);
      deferred.reject(error);
    } finally {
      this.#adding = false;
    }
  }
}

if (!customElements.get('upgrade-product-component')) {
  customElements.define('upgrade-product-component', UpgradeProductComponent);
}
